import React, { useEffect, useState } from "react";
import {
  Megaphone,
  CalendarDays,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  Users,
  Search,
  Plus,
  Pencil,
  Trash2,
  Eye,
  ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useAdminRole } from "../hooks/useAdminRole.ts";
import socioService from "../services/socioService.tsx";
import { Socio } from "../types/Socio.tsx";
import {
  listarCampanias,
  eliminarCampania,
  listarSociosPorCampania,
  asociarSociosBulk,
  eliminarAsociacionCampania,
  Campania,
  SocioEnCampania,
} from "../services/campaniaService.ts";

const estadoBadge = (estado: string) => {
  switch (estado) {
    case "activa":
      return "bg-emerald-100 text-emerald-700";
    case "borrador":
      return "bg-amber-100 text-amber-700";
    case "finalizada":
      return "bg-slate-100 text-slate-600";
    case "cancelada":
      return "bg-rose-100 text-rose-700";
    default:
      return "bg-slate-100 text-slate-600";
  }
};

export default function CampaniasGestor() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { isAdmin } = useAdminRole();
  const [campanias, setCampanias] = useState<Campania[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("");
  const [filtroEstado, setFiltroEstado] = useState<string>("");

  // Modal detalle socios
  const [selectedCampania, setSelectedCampania] = useState<Campania | null>(null);
  const [sociosEnCampania, setSociosEnCampania] = useState<SocioEnCampania[]>([]);
  const [loadingSocios, setLoadingSocios] = useState(false);

  // Modal agregar socios
  const [showAddSocios, setShowAddSocios] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<Socio[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedSocios, setSelectedSocios] = useState<number[]>([]);
  const [guardando, setGuardando] = useState(false);

  const [msg, setMsg] = useState<{ tipo: "ok" | "error"; texto: string } | null>(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate("/campanias");
      return;
    }
    loadCampanias();
  }, [isAdmin]);

  const loadCampanias = async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const data = await listarCampanias(undefined, token);
      setCampanias(data);
    } catch (e) {
      console.error("Error al cargar campañas:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleEliminar = async (campaniaId: number) => {
    if (!window.confirm("¿Cancelar esta campaña?")) return;
    try {
      const token = await getAccessTokenSilently();
      await eliminarCampania(campaniaId, token);
      setMsg({ tipo: "ok", texto: "Campaña cancelada" });
      loadCampanias();
    } catch (e) {
      setMsg({ tipo: "error", texto: "Error al cancelar campaña" });
    }
  };

  const handleVerSocios = async (campania: Campania) => {
    setSelectedCampania(campania);
    setLoadingSocios(true);
    try {
      const token = await getAccessTokenSilently();
      const socios = await listarSociosPorCampania(campania.campania_id, token);
      setSociosEnCampania(socios);
    } catch (e) {
      console.error("Error al cargar socios:", e);
    } finally {
      setLoadingSocios(false);
    }
  };

  const handleSearchSocios = async (term: string) => {
    setSearchTerm(term);
    if (term.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const results = await socioService.searchSocios(term, getAccessTokenSilently);
      const yaEnCampania = sociosEnCampania.map((s) => s.socio_id);
      setSearchResults(
        (Array.isArray(results) ? results : []).filter(
          (s: Socio) => !yaEnCampania.includes(Number(s.socio_id))
        )
      );
    } catch (e) {
      console.error("Error al buscar socios:", e);
    } finally {
      setSearching(false);
    }
  };

  const handleToggleSocio = (socioId: number) => {
    setSelectedSocios((prev) =>
      prev.includes(socioId) ? prev.filter((id) => id !== socioId) : [...prev, socioId]
    );
  };

  const handleAsociarSocios = async () => {
    if (!selectedCampania || selectedSocios.length === 0) return;
    setGuardando(true);
    try {
      const token = await getAccessTokenSilently();
      await asociarSociosBulk(
        { socioIds: selectedSocios, campaniaId: selectedCampania.campania_id },
        token
      );
      setMsg({ tipo: "ok", texto: `${selectedSocios.length} socios agregados a la campaña` });
      setShowAddSocios(false);
      setSelectedSocios([]);
      setSearchTerm("");
      setSearchResults([]);
      handleVerSocios(selectedCampania);
    } catch (e) {
      setMsg({ tipo: "error", texto: "Error al asociar socios" });
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminarAsociacion = async (socioCampaniaId: number) => {
    if (!window.confirm("¿Eliminar este socio de la campaña?")) return;
    try {
      const token = await getAccessTokenSilently();
      await eliminarAsociacionCampania(socioCampaniaId, token);
      setSociosEnCampania((prev) => prev.filter((s) => s.socio_campania_id !== socioCampaniaId));
      setMsg({ tipo: "ok", texto: "Socio eliminado de la campaña" });
    } catch (e) {
      setMsg({ tipo: "error", texto: "Error al eliminar socio" });
    }
  };

  const campaniasFiltradas = campanias.filter((c) => {
    const matchNombre = c.nombre.toLowerCase().includes(filtro.toLowerCase());
    const matchEstado = !filtroEstado || c.estado === filtroEstado;
    return matchNombre && matchEstado;
  });

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Gestión de Campañas</h1>
            <p className="mt-1 text-sm text-slate-500">Administra las campañas y sus socios asociados</p>
          </div>
          <button
            onClick={() => navigate("/campanias-nueva")}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" /> Nueva campaña
          </button>
        </div>

        {msg && (
          <div
            className={`mb-6 flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium ${
              msg.tipo === "ok"
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : "bg-rose-50 text-rose-700 border border-rose-200"
            }`}
          >
            {msg.tipo === "ok" ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
            {msg.texto}
          </div>
        )}

        {/* Filtros */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por nombre..."
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-700 transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
          >
            <option value="">Todos los estados</option>
            <option value="borrador">Borrador</option>
            <option value="activa">Activa</option>
            <option value="finalizada">Finalizada</option>
            <option value="cancelada">Cancelada</option>
          </select>
        </div>

        {/* Tabla */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : campaniasFiltradas.length === 0 ? (
          <div className="rounded-2xl border border-slate-100 bg-white/70 p-12 text-center shadow-sm">
            <Megaphone className="mx-auto h-12 w-12 text-slate-300" />
            <p className="mt-4 text-sm text-slate-500">No se encontraron campañas</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-lg">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="px-4 py-3 font-semibold text-slate-700">Campaña</th>
                    <th className="px-4 py-3 font-semibold text-slate-700">Período</th>
                    <th className="px-4 py-3 font-semibold text-slate-700">Estado</th>
                    <th className="px-4 py-3 font-semibold text-slate-700">Descuento</th>
                    <th className="px-4 py-3 font-semibold text-slate-700 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {campaniasFiltradas.map((campania) => (
                    <tr key={campania.campania_id} className="border-b border-slate-50 transition hover:bg-blue-50/30">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {campania.logo_url ? (
                            <img src={campania.logo_url} alt="" className="h-9 w-9 rounded-lg object-contain" />
                          ) : (
                            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600">
                              <Megaphone className="h-4 w-4 text-white" />
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-slate-900">{campania.nombre}</p>
                            {campania.descripcion && (
                              <p className="text-xs text-slate-500 line-clamp-1">{campania.descripcion}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1 text-slate-600">
                          <CalendarDays className="h-3.5 w-3.5" />
                          {new Date(campania.fecha_inicio).toLocaleDateString("es-AR")} - {new Date(campania.fecha_fin).toLocaleDateString("es-AR")}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${estadoBadge(campania.estado)}`}>
                          {campania.estado}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{campania.descuento || "-"}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => navigate(`/campanias/${campania.campania_id}`)}
                            className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                            title="Ver"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/campanias-editar/${campania.campania_id}`)}
                            className="rounded-lg p-2 text-slate-400 transition hover:bg-blue-100 hover:text-blue-700"
                            title="Editar"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleVerSocios(campania)}
                            className="rounded-lg p-2 text-slate-400 transition hover:bg-indigo-100 hover:text-indigo-700"
                            title="Ver socios"
                          >
                            <Users className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEliminar(campania.campania_id)}
                            className="rounded-lg p-2 text-slate-400 transition hover:bg-rose-100 hover:text-rose-700"
                            title="Cancelar"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modal Socios de Campaña */}
        {selectedCampania && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-900">
                  Socios en "{selectedCampania.nombre}"
                </h2>
                <button
                  onClick={() => { setSelectedCampania(null); setSociosEnCampania([]); setShowAddSocios(false); }}
                  className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>

              <button
                onClick={() => setShowAddSocios(true)}
                className="mb-4 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
              >
                <Plus className="h-4 w-4" /> Agregar socios
              </button>

              {showAddSocios && (
                <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 p-4">
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Buscar socio por nombre, DNI o CUIT..."
                      value={searchTerm}
                      onChange={(e) => handleSearchSocios(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm"
                    />
                  </div>
                  {searching && <Loader2 className="h-5 w-5 animate-spin text-blue-600" />}
                  {searchResults.length > 0 && (
                    <div className="max-h-48 space-y-1 overflow-y-auto">
                      {searchResults.map((s) => (
                        <label
                          key={s.socio_id}
                          className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 transition hover:bg-white"
                        >
                          <input
                            type="checkbox"
                            checked={selectedSocios.includes(Number(s.socio_id))}
                            onChange={() => handleToggleSocio(Number(s.socio_id))}
                            className="h-4 w-4 rounded text-blue-600"
                          />
                          <div>
                            <p className="text-sm font-medium text-slate-900">{s.socio_nombre}</p>
                            <p className="text-xs text-slate-500">{s.socio_mail}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                  {selectedSocios.length > 0 && (
                    <button
                      disabled={guardando}
                      onClick={handleAsociarSocios}
                      className="mt-3 inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                      Agregar {selectedSocios.length} socio(s)
                    </button>
                  )}
                </div>
              )}

              {loadingSocios ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : sociosEnCampania.length === 0 ? (
                <p className="py-4 text-center text-sm text-slate-500">No hay socios asociados a esta campaña</p>
              ) : (
                <div className="space-y-2">
                  {sociosEnCampania.map((s) => (
                    <div key={s.socio_campania_id} className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3 transition hover:bg-slate-50">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{s.socio_nombre || `Socio #${s.socio_id}`}</p>
                        <p className="text-xs text-slate-500">{s.socio_mail}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${
                          s.estado === "aceptada"
                            ? "bg-emerald-100 text-emerald-700"
                            : s.estado === "rechazada"
                            ? "bg-rose-100 text-rose-700"
                            : "bg-amber-100 text-amber-700"
                        }`}>
                          {s.estado === "aceptada" && <CheckCircle2 className="h-3 w-3" />}
                          {s.estado === "rechazada" && <XCircle className="h-3 w-3" />}
                          {s.estado === "pendiente" && <Clock className="h-3 w-3" />}
                          {s.estado}
                        </span>
                        <button
                          onClick={() => handleEliminarAsociacion(s.socio_campania_id)}
                          className="rounded-lg p-1.5 text-slate-400 transition hover:bg-rose-100 hover:text-rose-700"
                          title="Eliminar"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
